import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CarFront, Mail, MapPin, Navigation, Phone, TramFront } from "lucide-react";
import { API } from "@/lib/api";
import {
  DAY_LABELS,
  DAY_ORDER,
  addressLine,
  dayKey,
  isPlaceholder,
  isReachableEmail,
  isReachablePhone,
  mapsDirectionsUrl,
  safeImageUrl,
} from "@/lib/hours";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import OpenStatus from "@/components/OpenStatus";
import LocationName from "@/components/LocationName";
import MapConsent from "@/components/MapConsent";
import NotFound from "@/pages/NotFound";

const SCHEMA_DAYS = { mon: "Mo", tue: "Tu", wed: "We", thu: "Th", fri: "Fr", sat: "Sa", sun: "Su" };

function buildJsonLd(loc, origin, path) {
  const { street, city } = addressLine(loc);
  const address = {
    "@type": "PostalAddress",
    addressCountry: "DE",
    addressRegion: loc.state,
    addressLocality: loc.city,
  };
  if (!isPlaceholder(street) && street) address.streetAddress = street;
  if (!isPlaceholder(loc.postal_code) && loc.postal_code) address.postalCode = loc.postal_code;

  const openingHours = DAY_ORDER.flatMap((day) =>
    (loc.opening_hours?.[day] || []).map((r) => `${SCHEMA_DAYS[day]} ${r.open}-${r.close}`),
  );

  return [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: loc.name,
      url: `${origin}${path}`,
      address,
      ...(loc.lat != null && loc.lng != null
        ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
        : {}),
      ...(isReachablePhone(loc.phone) ? { telephone: loc.phone } : {}),
      ...(openingHours.length ? { openingHours } : {}),
      parentOrganization: { "@type": "Organization", name: "Sportwetten NRW GmbH" },
      brand: { "@type": "Brand", name: "Tipwin" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: origin },
        { "@type": "ListItem", position: 2, name: "Standorte", item: `${origin}/standorte` },
        { "@type": "ListItem", position: 3, name: loc.city || loc.name, item: `${origin}${path}` },
      ],
    },
  ];
}

export default function StandortDetail() {
  const { slug } = useParams();
  const [location, setLocation] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    API.get(`/locations/${slug}`)
      .then((res) => {
        setLocation(res.data);
        setState("ok");
        window.__swnPageReady = true;
      })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setState("notfound");
          window.__swnPageReady = true;
        } else {
          setState("error");
          window.__swnPageError = true;
        }
      });
  }, [slug]);

  const path = `/standorte/${slug}`;
  const jsonLd = useMemo(
    () => (location ? buildJsonLd(location, window.location.origin, path) : null),
    [location, path],
  );

  if (state === "notfound") return <NotFound />;
  if (state === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-28 text-center sm:px-6" data-testid="location-error">
        <h1 className="font-display text-2xl font-bold text-[#F5F5F3]">Verbindungsfehler</h1>
        <p className="mt-3 text-[#A1A1AA]">Die Standortdaten konnten nicht aktualisiert werden.</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-8 inline-flex min-h-[48px] items-center justify-center rounded-lg bg-[#E11D2A] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#F02837]"
          data-testid="location-retry-button"
        >
          Erneut versuchen
        </button>
      </section>
    );
  }
  if (state === "loading" || !location) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-12" aria-hidden="true" data-testid="location-loading">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-3">
            <div className="aspect-[16/9] w-full rounded-xl bg-[#1E1E24]" />
            <div className="h-9 w-2/3 rounded bg-[#1E1E24]" />
            <div className="h-4 w-1/3 rounded bg-[#1E1E24]" />
            <div className="h-24 w-full rounded bg-[#1E1E24]" />
          </div>
          <div className="space-y-6 lg:col-span-2">
            <div className="h-72 w-full rounded-xl bg-[#1E1E24]" />
            <div className="aspect-[16/9] w-full rounded-xl bg-[#1E1E24]" />
          </div>
        </div>
      </div>
    );
  }

  const { street, city } = addressLine(location);
  const today = dayKey(new Date());
  const mapQuery = `${location.name} ${street} ${city}`.trim();

  return (
    <>
      <Seo
        title={location.seo_title || `${location.name} | Sportwetten NRW`}
        description={location.seo_description || location.short_description}
        path={path}
        jsonLd={jsonLd}
      />

      <nav aria-label="Brotkrumen" className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-12">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-[#A1A1AA]">
          <li><Link to="/" className="hover:text-[#F5F5F3]" data-testid="breadcrumb-home">Start</Link></li>
          <li aria-hidden="true">/</li>
          <li><Link to="/standorte" className="hover:text-[#F5F5F3]" data-testid="breadcrumb-standorte">Standorte</Link></li>
          <li aria-hidden="true">/</li>
          <li className="text-[#F5F5F3]" aria-current="page">{location.city || location.name}</li>
        </ol>
      </nav>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-12" data-testid="location-detail">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <div>
              <div className="relative overflow-hidden rounded-xl border border-[#27272A]">
                {safeImageUrl(location.hero_image?.url) && (
                  <img
                    src={safeImageUrl(location.hero_image?.url)}
                    alt={location.hero_image.alt || location.name}
                    width="1200"
                    height="675"
                    fetchPriority="high"
                    className="aspect-[16/9] w-full object-cover"
                    data-testid="location-hero-image"
                  />
                )}
              </div>
              <h1 className="mt-8 font-display text-3xl font-extrabold tracking-tight text-[#F5F5F3] sm:text-4xl" data-testid="location-name">
                <LocationName name={location.name} tone="dark" />
              </h1>
              <div className="mt-3"><OpenStatus location={location} /></div>
              <p className="mt-4 flex items-start gap-2 text-[#A1A1AA]" data-testid="location-address">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-[#F54855]" aria-hidden="true" />
                <span>
                  {street} <br /> {city}
                  {isPlaceholder(location.postal_code) && (
                    <span className="mt-1 block text-xs italic text-amber-400/80">Adresse wird derzeit verifiziert.</span>
                  )}
                </span>
              </p>

              <div className="mt-6 flex flex-wrap gap-3">
                {isReachablePhone(location.phone) && (
                  <a
                    href={`tel:${location.phone.replace(/[\s\-/()]/g, "")}`}
                    className="inline-flex min-h-[48px] items-center gap-2 rounded-lg bg-[#E11D2A] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#F02837]"
                    data-testid="location-phone-link"
                  >
                    <Phone className="h-4 w-4" aria-hidden="true" /> {location.phone}
                  </a>
                )}
                {isReachableEmail(location.email) && (
                  <a
                    href={`mailto:${location.email}`}
                    className="inline-flex min-h-[48px] items-center gap-2 rounded-lg border border-[#27272A] px-5 py-3 text-sm font-semibold text-[#F5F5F3] transition-colors hover:border-[#E11D2A]/40 hover:bg-[#1E1E24]"
                    data-testid="location-email-link"
                  >
                    <Mail className="h-4 w-4" aria-hidden="true" /> E-Mail schreiben
                  </a>
                )}
                <a
                  href={mapsDirectionsUrl(location)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[48px] items-center gap-2 rounded-lg border border-[#27272A] px-5 py-3 text-sm font-semibold text-[#F5F5F3] transition-colors hover:border-[#E11D2A]/40 hover:bg-[#1E1E24]"
                  data-testid="location-route-link"
                >
                  <Navigation className="h-4 w-4" aria-hidden="true" /> Route planen
                </a>
              </div>

              {location.long_description && (
                <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#A1A1AA]">{location.long_description}</p>
              )}

              {location.services?.length > 0 && (
                <div className="mt-8">
                  <h2 className="font-display text-xl font-semibold text-[#F5F5F3]">Service vor Ort</h2>
                  <ul className="mt-4 flex flex-wrap gap-2" data-testid="location-services">
                    {location.services.map((service) => (
                      <li key={service} className="rounded-full border border-[#27272A] bg-[#16161A] px-4 py-2 text-sm text-[#F5F5F3]">
                        {service}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div>
              <div className="rounded-xl border border-[#27272A] bg-[#16161A] p-6" data-testid="location-hours">
                <h2 className="font-display text-xl font-semibold text-[#F5F5F3]">Öffnungszeiten</h2>
                <table className="mt-4 w-full text-sm">
                  <tbody>
                    {DAY_ORDER.map((day) => {
                      const ranges = location.opening_hours?.[day] || [];
                      const isToday = day === today;
                      return (
                        <tr key={day} className={isToday ? "text-[#F5F5F3]" : "text-[#A1A1AA]"} data-testid={`hours-row-${day}`}>
                          <td className="py-2 pr-4 font-medium">
                            {DAY_LABELS[day]}
                            {isToday && <span className="ml-2 rounded bg-[#E11D2A]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#F54855]">Heute</span>}
                          </td>
                          <td className="py-2 text-right tabular-nums">
                            {ranges.length === 0
                              ? "Geschlossen"
                              : ranges.map((r) => `${r.open}–${r.close} Uhr`).join(", ")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {location.special_hours?.length > 0 && (
                  <div className="mt-4 border-t border-[#27272A] pt-4">
                    <h3 className="text-sm font-semibold text-[#F5F5F3]">Sonderöffnungszeiten</h3>
                    <ul className="mt-2 space-y-1 text-sm text-[#A1A1AA]" data-testid="location-special-hours">
                      {location.special_hours.map((s) => (
                        <li key={s.date}>
                          {s.date}: {s.closed ? "Geschlossen" : `${s.open}–${s.close} Uhr`}
                          {s.note ? ` · ${s.note}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="mt-4 text-xs italic text-[#96908B]">An stillen Feiertagen (u. a. Karfreitag, Volkstrauertag, Totensonntag) bleiben wir geschlossen. An anderen gesetzlichen Feiertagen können die Öffnungszeiten abweichen.</p>
              </div>

              {(location.directions_car || location.directions_transit || location.parking_info) && (
                <div className="mt-6 rounded-xl border border-[#27272A] bg-[#16161A] p-6" data-testid="location-directions">
                  <h2 className="font-display text-xl font-semibold text-[#F5F5F3]">Anfahrt</h2>
                  {location.directions_car && (
                    <p className="mt-3 flex gap-2 text-sm text-[#A1A1AA]"><CarFront className="mt-0.5 h-4 w-4 shrink-0 text-[#F54855]" aria-hidden="true" />{location.directions_car}</p>
                  )}
                  {location.directions_transit && (
                    <p className="mt-3 flex gap-2 text-sm text-[#A1A1AA]"><TramFront className="mt-0.5 h-4 w-4 shrink-0 text-[#F54855]" aria-hidden="true" />{location.directions_transit}</p>
                  )}
                  {location.parking_info && (
                    <p className="mt-3 text-sm text-[#A1A1AA]"><span className="font-medium text-[#F5F5F3]">Parken:</span> {location.parking_info}</p>
                  )}
                </div>
              )}

              <div className="mt-6">
                <MapConsent query={mapQuery} title={location.name} />
              </div>
            </div>
          </div>
        </div>

        {location.gallery?.length > 0 && (
          <Reveal className="mt-14">
            <h2 className="font-display text-2xl font-bold tracking-tight text-[#F5F5F3]">Einblicke</h2>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="location-gallery">
              {location.gallery.filter((img) => safeImageUrl(img.url)).map((img, i) => (
                <div key={`${img.url}-${i}`} className="overflow-hidden rounded-xl border border-[#27272A] bg-[#16161A]">
                  <img src={img.url} alt={img.alt || location.name} width="800" height="450" loading="lazy" className="aspect-[16/9] w-full object-cover" />
                </div>
              ))}
            </div>
          </Reveal>
        )}
      </section>
    </>
  );
}
